// Shared helpers for deciding where to send a user after auth
// based on their role and onboarding/profile completion state.

// Mongoose may return empty sub-documents like {} after save.
// Only count a profile as "existing" when it has real data.
// Skip numeric 0, false, and known schema-default enum values.
const SCHEMA_DEFAULTS = new Set([
  'roas','quality','available','0','false',
]);

const hasContent = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.keys(obj).some((key) => {
    if (key === '_id' || key === '__v' || key.startsWith('$')) return false;
    const val = obj[key];
    if (val === undefined || val === null || val === '' || val === 0 || val === false) return false;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'object') return hasContent(val);
    if (typeof val === 'string' && SCHEMA_DEFAULTS.has(val)) return false;
    return true;
  });
};

export const getPostAuthPath = (user) => {
  if (!user) return '/login';

  const role = user.role;
  const profileComplete = user.profileComplete;
  const legacyComplete =
    hasContent(user.creatorProfile) ||
    hasContent(user.freelancerProfile) ||
    hasContent(user.brandProfile) ||
    hasContent(user.agencyProfile) ||
    hasContent(user.campaignManagerProfile);
  const isIncomplete = profileComplete !== true && legacyComplete !== true;

  const isAdmin = ['admin', 'super-admin', 'owner'].includes(role);
  if (isAdmin) {
    return '/admin';
  }

  if (role === 'tech_support') {
    return '/admin/support';
  }

  // If profile is complete but account is not approved, show pending screen
  const accountStatus = user.accountStatus;
  const needsApproval = accountStatus && accountStatus !== 'approved';

  switch (role) {
    case 'creator':
      if (isIncomplete) return '/onboarding';
      if (needsApproval) return '/pending-approval';
      return '/creator/dashboard';
    case 'freelancer':
      if (isIncomplete) return '/onboarding';
      if (needsApproval) return '/pending-approval';
      return '/freelancer/dashboard';
    case 'brand':
      if (isIncomplete) return '/onboarding';
      if (needsApproval) return '/pending-approval';
      return '/brand/dashboard';
    case 'agency':
      if (isIncomplete) return '/onboarding';
      if (needsApproval) return '/pending-approval';
      return '/agency/dashboard';
    case 'campaign_manager':
      if (isIncomplete) return '/onboarding';
      if (needsApproval) return '/pending-approval';
      return '/dashboard';
    case 'unassigned':
    default:
      return '/role-selection';
  }
};
